import { FC, memo, useMemo } from 'react';
import { Button } from '@mui/material';
import { AxiosError } from 'axios';
import { ReactComponent as TrashService } from 'assets/icons/trash.svg';
import { EditIconButton } from 'components/elements';
import { IOption } from 'components/elements/CustomSelect';
import toastService from 'components/elements/Toastify';
import ConfirmationModal from 'components/modules/ConfirmationModal';
import { ICityFormValues, ICityItem, IErrorResponse } from 'types';
import { Rights } from 'utils/enums/rights.enum';
import { getErrorMessage } from 'utils/helpers';
import { useModal } from 'utils/hooks';
import { usePermissions } from 'utils/hooks/use-permissions';
import { CitiesService } from 'utils/services/cities.service';
import { ManageCityModal } from '../ManageCityModal';

const DEVICE_FP = '6037e65eefbdasdfadsfasdf3';

interface UpdateCityAnchorProps {
	cityRow: ICityItem;
	onSuccess: () => Promise<void>;
	regionOptions: IOption[];
	regionOptionsLoading: boolean;
	onRegionSearch: (search: string) => Promise<void>;
	prefixOptions: IOption[];
	shortPrefixOptions: IOption[];
}

export const UpdateCityAnchor: FC<UpdateCityAnchorProps> = memo(
	({ cityRow, onSuccess, regionOptions, regionOptionsLoading, onRegionSearch, prefixOptions, shortPrefixOptions }) => {
		const [isOpen, handleOpen, handleClose] = useModal();
		const [isDeleteModalOpen, handleOpenDeleteModal, handleCloseDeleteModal] = useModal();
		const checkRights = usePermissions();

		const initialValues = useMemo(
			() => ({
				name: cityRow.name,
				shorPrefix: cityRow.shorPrefix,
				prefix: cityRow.prefix,
				regionId: cityRow.regionId,
			}),
			[cityRow]
		);

		const canDelete = checkRights(Rights.FULL_ACCESS);

		const handleUpdateModal = async (values: ICityFormValues) => {
			try {
				await CitiesService.updateCity({
					id: cityRow.id,
					name: values.name.trim(),
					shorPrefix: values.shorPrefix.trim(),
					prefix: values.prefix.trim(),
					regionId: values.regionId,
					deviceFP: DEVICE_FP,
				});
				toastService.success('Місто успішно оновлено');
				handleClose();
				await onSuccess();
			} catch (error) {
				toastService.error(getErrorMessage(error as AxiosError<IErrorResponse>));
			}
		};

		const handleOpenModal = () => {
			void onRegionSearch(cityRow.region?.name || '');
			handleOpen();
		};

		const handleOpenDelete = () => {
			handleClose();
			handleOpenDeleteModal();
		};

		const handleDeleteCity = async () => {
			try {
				await CitiesService.deleteCity({
					id: cityRow.id,
					deviceFP: DEVICE_FP,
				});
				toastService.success('Місто успішно видалено');
				handleCloseDeleteModal();
				await onSuccess();
			} catch (error) {
				toastService.error(getErrorMessage(error as AxiosError<IErrorResponse>));
			}
		};

		return (
			<>
				<EditIconButton onClick={handleOpenModal} />
				<ManageCityModal
					title="Оновити місто"
					isOpen={isOpen}
					onClose={handleClose}
					initialValues={initialValues}
					onSubmit={handleUpdateModal}
					buttonText="Оновити"
					regionOptions={regionOptions}
					regionOptionsLoading={regionOptionsLoading}
					onRegionSearch={onRegionSearch}
					prefixOptions={prefixOptions}
					shortPrefixOptions={shortPrefixOptions}
					initialRegionTitle={cityRow.region?.name || ''}
					canDelete={canDelete}
					onDeleteRequest={handleOpenDelete}
				/>
				<ConfirmationModal
					open={isDeleteModalOpen}
					onClose={handleCloseDeleteModal}
					title={`Видалити місто ${cityRow.name}?`}
					message="Після видалення місто не можна буде відновити."
					modalIcon={<TrashService />}
				>
					<div style={{ display: 'flex', gap: '10px' }}>
						<Button onClick={handleCloseDeleteModal} style={{ borderRadius: '5px' }} variant="contained" color="primary">
							Скасувати
						</Button>
						<Button onClick={handleDeleteCity} style={{ borderRadius: '5px' }} variant="contained" color="secondary">
							Видалити
						</Button>
					</div>
				</ConfirmationModal>
			</>
		);
	}
);
